import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { isValidEmail, validateName } from "../profileOptions";
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "./legalLinks";
import { LogInPrompt } from "./LogInPrompt";

// ACA-266 AC5/AC6: step 3 of the rebuilt wizard (the design's fourth step, "Get started", is a
// separate file). Copy, field list and the sentence that fuses the AC4 confirmation with the AC6
// plan message are App Onboarding.dc.html's `isDetails` block, read verbatim.
//
// Presentational only: it takes what a container already holds - the goals picked in step 2, an
// account submitted before, a server rejection - and hands a finished GalleryAccountDetails back
// through onSubmit. Nothing here reaches the network; POST api/onboarding/conversions and its two
// request headers (§5.1 of the plan) belong to that container.
//
// Parametrized (ACA-267 Phase 4) so the same component serves three personas - gallery, artist
// and Collector. Every prop defaults to the gallery's own values, which is what keeps this
// file's existing tests passing unchanged.
// Where GalleryPracticeStep's artist gained a section, this step's artist loses one: Gallery
// Name and Gallery Website (`accountOrgFields`) render only for the gallery, because the artist
// already collects the matching pair - Studio Name and Artist Website - one step earlier, behind
// GalleryPracticeStep's `practiceFields`. Hiding the two fields is enough to produce the artist's
// three clean pairs; nothing else in the field order changes.
//
// Like GalleryPracticeStep, this is a leaf: it takes copy through props rather than importing
// personaCopy.ts. `orgLabel`/`orgPlaceholder`/`siteLabel`/`sitePlaceholder` look pointless for the
// artist, who never renders them here - but they are the same personaCopy.ts columns that also
// feed GalleryPracticeStep's step-2 section, so keeping them as props (rather than gallery
// literals) is what keeps that table the one source for the gallery's copy.

const HOW_DID_YOU_HEAR_OPTIONS: readonly string[] = [
  "Google",
  "Instagram",
  "A friend or colleague",
  "Art fair or event",
  "Social media",
  "Other",
];

const MIN_PASSWORD_LENGTH = 8;

export interface GalleryAccountDetails {
  email: string;
  fullName: string;
  galleryName: string;
  website: string;
  phone: string;
  password: string;
  /**
   * Typed, checked here, and never sent: POST api/onboarding/conversions has no field for it.
   * It is carried in the details anyway so a 3 -> 2 -> 3 round trip redraws the screen as the
   * visitor left it, the same reason `password` is carried.
   */
  confirmPassword: string;
  howDidYouHear: string;
  hasAgreedToTerms: boolean;
}

const EMPTY_DETAILS: GalleryAccountDetails = {
  email: "",
  fullName: "",
  galleryName: "",
  website: "",
  phone: "",
  password: "",
  confirmPassword: "",
  howDidYouHear: "",
  hasAgreedToTerms: false,
};

export interface GalleryAccountStepProps {
  /** What the visitor already typed, or what a rehydrated ghost already has on file. Read once, at mount - not a controlled value. */
  initialValues?: Partial<GalleryAccountDetails>;
  /** The last submit's server rejection (§5.2's 400s), shown as the server wrote it. Not a 409 on
      this endpoint for the artist any more - see `emailError` below. The gallery's own endpoint
      (POST /conversions) has no equivalent inline treatment, so a 409 there still lands here. */
  error?: string;
  /**
   * ACA-267 Phase 5/6: a 409 duplicate-email rejection, shown inline under the email input rather
   * than in the `error` banner above - the live screen's own treatment
   * (CreateAccountScreen.tsx's `form.setFieldError("email", ...)`), which this ports because
   * fidelity to that screen is this ticket's tie-breaker. Local validation (`errors.email`, an
   * empty field) takes precedence when both would apply - a blank submit shows "Enter your email
   * address.", never this. Dismissed as soon as the visitor edits the email: Mantine gives the
   * legacy screen that behaviour for free (revalidate-on-change clears a field error); this
   * component fakes it with its own dismiss flag below, which the effect re-arms whenever this
   * prop takes on a new value (the container clears its own state to undefined at the start of
   * every submit, the same way it already does for `error`, so a second rejection - even with the
   * same message - is a value change this component can see: undefined, then the message again).
   */
  emailError?: string;
  loading?: boolean;
  /**
   * Back to GalleryPracticeStep. The design's label is the previous step's own name, not
   * "Practice". Carries whatever is currently in the fields, unvalidated - the caller's the one
   * that decides whether to hold onto it, and a 3 -> 2 -> 3 round trip is the case that needs it.
   */
  onBack: (details: GalleryAccountDetails) => void;
  onSubmit: (details: GalleryAccountDetails) => void;
  /**
   * Renders the Gallery Name / Gallery Website fields. Defaults to true for the gallery. False
   * for the artist, who collects the matching pair - Studio Name and Artist Website - one step
   * earlier, behind GalleryPracticeStep's `practiceFields`; rendering both would ask for the same
   * two facts twice.
   */
  accountOrgFields?: boolean;
  /**
   * Runs validateName's full format check (length, character set) on Full Name, the way
   * OnboardingProfileScreen already does for the artist. False for the gallery (ACA-267 FIX-1):
   * the legacy RegistrationForm's Full Name was `type="text"`, un-`required` and unvalidated, so
   * the only check that ever applied to it was "not blank" - "Enter your name." below. True only
   * for the artist, via ARTIST_PERSONA_COPY.
   */
  validateFullNameFormat?: boolean;
  /** Label beside the Back chevron - the previous step's own name, the way the design writes it. */
  backLabel?: string;
  emailPlaceholder?: string;
  /** Read by `accountOrgFields`'s two fields. Not used by the artist screen (accountOrgFields is
      false there), but kept as a prop rather than a gallery literal: this is the same
      personaCopy.ts column that also feeds GalleryPracticeStep's step-2 organisation section. */
  orgLabel?: string;
  orgPlaceholder?: string;
  siteLabel?: string;
  sitePlaceholder?: string;
}

/**
 * What the tracker's own step links need and a plain onBack prop cannot give them: they live in
 * OnboardingV2Stage, outside this component's tree, and do not go through the Back button's click
 * handler - so a container that wants the same capture on a tracker-driven 3 -> 2 (D5) reads it
 * through this handle instead. Mirrors GalleryPracticeStepHandle.
 */
export interface GalleryAccountStepHandle {
  captureDetails: () => GalleryAccountDetails;
}

export const GalleryAccountStep = forwardRef<GalleryAccountStepHandle, GalleryAccountStepProps>(
  function GalleryAccountStep(
    {
      initialValues,
      error,
      emailError,
      loading,
      onBack,
      onSubmit,
      accountOrgFields = true,
      validateFullNameFormat = false,
      backLabel = "Your organisation",
      emailPlaceholder = "you@gallery.com",
      orgLabel = "Gallery Name",
      orgPlaceholder = "Gallery or business name",
      siteLabel = "Gallery Website",
      sitePlaceholder = "yourgallery.com",
    },
    ref,
  ) {
  const [email, setEmail] = useState(initialValues?.email ?? EMPTY_DETAILS.email);
  const [fullName, setFullName] = useState(initialValues?.fullName ?? EMPTY_DETAILS.fullName);
  const [galleryName, setGalleryName] = useState(
    initialValues?.galleryName ?? EMPTY_DETAILS.galleryName,
  );
  const [website, setWebsite] = useState(initialValues?.website ?? EMPTY_DETAILS.website);
  const [phone, setPhone] = useState(initialValues?.phone ?? EMPTY_DETAILS.phone);
  const [password, setPassword] = useState(initialValues?.password ?? EMPTY_DETAILS.password);
  const [confirmPassword, setConfirmPassword] = useState(
    initialValues?.confirmPassword ?? EMPTY_DETAILS.confirmPassword,
  );
  const [howDidYouHear, setHowDidYouHear] = useState(
    initialValues?.howDidYouHear ?? EMPTY_DETAILS.howDidYouHear,
  );
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(
    initialValues?.hasAgreedToTerms ?? EMPTY_DETAILS.hasAgreedToTerms,
  );
  // email/fullName hold the message to show, "" meaning no error - the message text varies
  // (blank vs. invalid-format say different things), unlike the other three fields below.
  const [errors, setErrors] = useState<{
    email: string;
    fullName: string;
    password: boolean;
    confirmPassword: boolean;
    terms: boolean;
  }>({
    email: "",
    fullName: "",
    password: false,
    confirmPassword: false,
    terms: false,
  });
  // See emailError's own doc comment above: re-armed whenever the container hands this component
  // a new value (including going back to the same message after clearing to undefined in between).
  const [emailErrorDismissed, setEmailErrorDismissed] = useState(false);
  useEffect(() => {
    if (emailError) {
      setEmailErrorDismissed(false);
    }
  }, [emailError]);
  const showEmailError = Boolean(emailError) && !emailErrorDismissed;

  const currentDetails = (): GalleryAccountDetails => ({
    email,
    fullName,
    galleryName,
    website,
    phone,
    password,
    confirmPassword,
    howDidYouHear,
    hasAgreedToTerms,
  });

  useImperativeHandle(ref, () => ({ captureDetails: currentDetails }));

  // AC6's plan message alone. AC4's confirmation of the goal picked - the design's
  // "We'll start you on ..." clause - was dropped in design review, so the lead no longer varies
  // with what was picked and the step does not need to be told.
  const lead = "Free accounts include core inventory tools. A 14-day trial unlocks everything.";

  // All checks run before onSubmit, and all at once: a screen with several ways to be
  // incomplete should not reveal the second only after the first is fixed (same rule as
  // GalleryPracticeStep). Email is validated for format on both personas. For the artist that
  // restores CreateAccountScreen's own EMAIL_REGEX; for the gallery it is new, and deliberately
  // so. The legacy form's `required`/`type="email"` are never enforced - page/Onboarding.jsx has
  // no <form>, no onSubmit and no checkValidity anywhere, and handleContinue checks only for
  // non-blank - so the gallery has never had a format check. The divergence is in the PR.
  // Full Name is not: the legacy RegistrationForm never validated it beyond non-blank, so only
  // `validateFullNameFormat` (true for the artist, false for the gallery) runs validateName's
  // format check here - both personas still require it non-blank. Password stays at the server's
  // 8, not the artist flow's four extra client-side rules. Gallery Name and Gallery Website are
  // not required: today's /register does not require them either, and they were optional on the
  // step they came from.
  //
  // The confirmation is only ever reported as a mismatch, never as "too short": a visitor who has
  // typed four characters in both gets one message about the password, not two about both boxes.
  const handleSubmit = () => {
    const passwordTooShort = password.length < MIN_PASSWORD_LENGTH;
    const trimmedEmail = email.trim();
    const trimmedFullName = fullName.trim();
    // Guarded on trimmedFullName here, not just on the blank-message branch below, so
    // validateName's own "Required" return can never be the value this reads: a blank name is
    // already routed to "Enter your name." before this ever runs.
    const fullNameFormatResult =
      validateFullNameFormat && trimmedFullName !== "" ? validateName(fullName) : undefined;
    const next = {
      email:
        trimmedEmail === ""
          ? "Enter your email address."
          : isValidEmail(trimmedEmail)
            ? ""
            : "Enter a valid email address.",
      fullName:
        trimmedFullName === ""
          ? "Enter your name."
          : fullNameFormatResult && !fullNameFormatResult.ok
            ? (fullNameFormatResult.msg ?? "")
            : "",
      password: passwordTooShort,
      confirmPassword: !passwordTooShort && confirmPassword !== password,
      terms: !hasAgreedToTerms,
    };
    setErrors(next);
    if (next.email || next.fullName || next.password || next.confirmPassword || next.terms) return;

    onSubmit({
      email: email.trim(),
      fullName: fullName.trim(),
      galleryName: galleryName.trim(),
      website: website.trim(),
      phone: phone.trim(),
      password,
      confirmPassword,
      howDidYouHear,
      hasAgreedToTerms,
    });
  };

  return (
    <div className="gallery-account">
      <button
        type="button"
        className="onboarding-v2__back"
        onClick={() => onBack(currentDetails())}
        // Onboarding.jsx:647's full-screen <ModalBackground /> blocked every control while
        // `loading`, this button included - the same rule Create Account already gets below, not
        // a new one invented for this button.
        disabled={loading || undefined}
        aria-busy={loading || undefined}
        data-testid="account-back"
      >
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m14 6-6 6 6 6" />
        </svg>
        <span>{backLabel}</span>
      </button>

      <h1 className="onboarding-v2__heading" data-testid="screen-heading">
        Last step &mdash; your login details.
      </h1>
      <p className="onboarding-v2__lead" data-testid="screen-subheading">
        {lead}
      </p>

      {error && (
        <p className="gallery-account__server-error" role="alert" data-testid="server-error">
          {error}
        </p>
      )}

      <div className="gallery-account__fields">
        <div className="gallery-account__field">
          <label className="gallery-account__label" htmlFor="account-email">
            {/* The asterisk is the design's mark for a required field and is decoration, the
                counterpart of the "(optional)" hint below; aria-required on the control is what a
                screen reader announces. Only the four fields handleSubmit actually holds the step
                for are marked - Gallery Name, Gallery Website and Phone Number are optional. */}
            Email{" "}
            <span className="gallery-account__required" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="account-email"
            className="gallery-account__input"
            type="email"
            autoComplete="email"
            placeholder={emailPlaceholder}
            value={email}
            aria-required="true"
            aria-invalid={(Boolean(errors.email) || showEmailError) || undefined}
            aria-describedby={errors.email || showEmailError ? "account-email-error" : undefined}
            onChange={(event) => {
              setEmail(event.target.value);
              setErrors((current) => ({ ...current, email: "" }));
              // The server's rejection is about the email already submitted - it must not survive
              // an edit to a different address nobody has tried yet.
              setEmailErrorDismissed(true);
            }}
            data-testid="account-email"
          />
          {errors.email ? (
            <p className="gallery-account__error" id="account-email-error" role="alert" data-testid="account-email-error">
              {errors.email}
            </p>
          ) : (
            showEmailError && (
              <p className="gallery-account__error" id="account-email-error" role="alert" data-testid="account-email-error">
                {emailError}
              </p>
            )
          )}
        </div>

        <div className="gallery-account__field">
          <label className="gallery-account__label" htmlFor="account-full-name">
            Full Name{" "}
            <span className="gallery-account__required" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="account-full-name"
            className="gallery-account__input"
            type="text"
            autoComplete="name"
            placeholder="Your full name"
            value={fullName}
            aria-required="true"
            aria-invalid={Boolean(errors.fullName) || undefined}
            aria-describedby={errors.fullName ? "account-full-name-error" : undefined}
            onChange={(event) => {
              setFullName(event.target.value);
              setErrors((current) => ({ ...current, fullName: "" }));
            }}
            data-testid="account-full-name"
          />
          {errors.fullName && (
            <p className="gallery-account__error" id="account-full-name-error" role="alert" data-testid="account-full-name-error">
              {errors.fullName}
            </p>
          )}
        </div>

        {accountOrgFields && (
          <>
            <div className="gallery-account__field">
              <label className="gallery-account__label" htmlFor="gallery-name">
                {orgLabel}
              </label>
              <input
                id="gallery-name"
                className="gallery-account__input"
                type="text"
                autoComplete="organization"
                placeholder={orgPlaceholder}
                value={galleryName}
                onChange={(event) => setGalleryName(event.target.value)}
                data-testid="gallery-name"
              />
            </div>

            <div className="gallery-account__field">
              <label className="gallery-account__label" htmlFor="gallery-website">
                {siteLabel}
              </label>
              <input
                id="gallery-website"
                className="gallery-account__input"
                type="text"
                inputMode="url"
                autoComplete="url"
                placeholder={sitePlaceholder}
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
                data-testid="gallery-website"
              />
            </div>
          </>
        )}

        <div className="gallery-account__field">
          <label className="gallery-account__label" htmlFor="account-password">
            Password{" "}
            <span className="gallery-account__required" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="account-password"
            className="gallery-account__input"
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            value={password}
            aria-required="true"
            aria-invalid={errors.password || undefined}
            aria-describedby={errors.password ? "account-password-error" : undefined}
            onChange={(event) => {
              setPassword(event.target.value);
              setErrors((current) => ({ ...current, password: false }));
            }}
            data-testid="account-password"
          />
          {errors.password && (
            <p className="gallery-account__error" id="account-password-error" role="alert" data-testid="account-password-error">
              Use at least 8 characters.
            </p>
          )}
        </div>

        {/* Sits beside Password, which is why it is here and not after Phone: the grid pairs
            fields two per row in DOM order, so the confirmation reads as the second half of the
            password, not as a field of its own. */}
        <div className="gallery-account__field">
          <label className="gallery-account__label" htmlFor="account-confirm-password">
            Confirm Password{" "}
            <span className="gallery-account__required" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="account-confirm-password"
            className="gallery-account__input"
            type="password"
            autoComplete="new-password"
            placeholder="Re-enter your password"
            value={confirmPassword}
            aria-required="true"
            aria-invalid={errors.confirmPassword || undefined}
            aria-describedby={errors.confirmPassword ? "account-confirm-password-error" : undefined}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              setErrors((current) => ({ ...current, confirmPassword: false }));
            }}
            data-testid="account-confirm-password"
          />
          {errors.confirmPassword && (
            <p className="gallery-account__error" id="account-confirm-password-error" role="alert" data-testid="account-confirm-password-error">
              Password and confirmation password must match.
            </p>
          )}
        </div>

        <div className="gallery-account__field">
          <label className="gallery-account__label" htmlFor="account-phone">
            Phone Number
          </label>
          <input
            id="account-phone"
            className="gallery-account__input"
            type="tel"
            autoComplete="tel"
            placeholder="(000)(000)(000)"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            data-testid="account-phone"
          />
        </div>

        <div className="gallery-account__field">
          <label className="gallery-account__label" htmlFor="account-how-did-you-hear">
            Where did you hear about us?{" "}
            <span className="gallery-account__hint">(optional)</span>
          </label>
          <select
            id="account-how-did-you-hear"
            className="gallery-account__select"
            value={howDidYouHear}
            onChange={(event) => setHowDidYouHear(event.target.value)}
            data-testid="account-how-did-you-hear"
          >
            <option value="">Select an option</option>
            {HOW_DID_YOU_HEAR_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="gallery-account__actions">
        <label className="gallery-account__terms" data-testid="terms-label">
          <input
            type="checkbox"
            className="gallery-account__terms-input"
            checked={hasAgreedToTerms}
            aria-required="true"
            aria-invalid={errors.terms || undefined}
            aria-describedby={errors.terms ? "account-terms-error" : undefined}
            onChange={(event) => {
              setHasAgreedToTerms(event.target.checked);
              setErrors((current) => ({ ...current, terms: false }));
            }}
            data-testid="terms-checkbox"
          />
          <span>
            Accept{" "}
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer" data-testid="terms-link">
              terms of service
            </a>{" "}
            and{" "}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" data-testid="privacy-link">
              privacy policy
            </a>
          </span>
        </label>
        {errors.terms && (
          <p className="gallery-account__error" id="account-terms-error" role="alert" data-testid="account-terms-error">
            Accept the terms of service to continue.
          </p>
        )}

        <div className="gallery-account__submit">
          <button
            type="button"
            className="onboarding-v2__cta"
            onClick={handleSubmit}
            disabled={loading || undefined}
            aria-busy={loading || undefined}
            data-testid="account-submit"
          >
            Create Account
          </button>
          <LogInPrompt />
        </div>
      </div>
    </div>
  );
  },
);
